import { Box, SxProps, Theme, Typography, styled } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { BadgeStates } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import * as Icons from "akar-icons";
import React from "react";

import { useMobileQuery } from "../utils";

interface IBannerProps {
  children: React.ReactNode;

  /**
   * Banner's background color & Color - Optional
   * @default 'default'
   * */
  state?: BadgeStates;

  className?: string;

  sx?: SxProps<Theme>;

  onClose?: () => any;

  /** Toggles icon visibility */
  useIcon?: boolean;
}

const Wrapper = styled(Box, {
  shouldForwardProp: prop => prop !== "isMobile",
})<{ isMobile: boolean }>`
  align-items: start;
  background-color: ${props => props.theme.modernBannerColor};
  border-radius: ${props => props.theme.modernCardRadius}px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 20px;
  min-height: 60px;
  padding: 20px;
  position: relative;
  text-align: start;

  & svg {
    height: ${props => (props.isMobile ? "20px" : "16px")};
    stroke: ${props => props.theme.modernIconsColor};
    width: ${props => (props.isMobile ? "20px" : "16px")};
    margin-right: 7px;
    margin-top: 2px;
  }

  &[class*="success"] {
    background-color: ${props => props.theme.modernActiveBadgeColor};
  }
  &[class*="error"] {
    background-color: ${props => props.theme.modernCancelledBadgeColor};
  }
  &[class*="warning"] {
    background-color: ${props => props.theme.modernPausedBadgeColor};
  }
`;

export const AquariusBanner = ({
  sx,
  children,
  state = "default",
  className,
  onClose,
  useIcon = true,
  ...props
}: IBannerProps): JSX.Element => {
  const isMobile = useMobileQuery();
  const IconName = (): JSX.Element | null => {
    switch (state) {
      case "warning": {
        return <Icons.TriangleAlert />;
      }
      case "error": {
        return <Icons.CircleAlert />;
      }
      case "success": {
        return <Icons.CircleCheck />;
      }
      default: {
        return null;
      }
    }
  };
  return (
    <Wrapper
      className={`${prefixClassName("banner")} ${prefixClassName(`status-${state}`)} ${className ?? ""}`}
      onClick={onClose ? () => onClose() : () => null}
      sx={{ cursor: onClose ? "pointer" : "auto", ...sx }}
      isMobile={isMobile}
      data-testid="portal-banner"
      {...props}
    >
      <Box component="section" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {!!IconName && useIcon ? <IconName /> : null}
      </Box>
      <Typography component="section" variant="body1">
        {children}
      </Typography>
    </Wrapper>
  );
};
